<template>
  <div>
    <div class="title-bar title-h3">
      {{ chartData.title }}
    </div>
    <div class="list-analytics">
      <div>
        <highcharts class="chart" :options="options" ref="highchart" />
        <el-scrollbar class="status">
          <ul class="list list-event">
            <li v-for="point in legendPoints" :key="point.key" :class="getLegendClass(point.series)" :style="{borderColor: point.color}" >
              <span class="number">{{ convertValuByUnit(point) }}</span>
              <span class="content" @click="onClickLegend(point.series)">
                <span>{{ convertLegendName(point.series) }}</span>
              </span>
            </li>
          </ul>
        </el-scrollbar>
      </div>
    </div>
  </div>
</template>

<script>
import { findLast } from 'lodash';
import { isEmpty } from '@/utils/basic';
import { convertValuByUnit, convertMillisecondsToSeconds } from '@/utils/number';
import { getDateToString } from '@/utils/date';
import { chartColorSet } from '@/constants/chart-options';

const options = {
  chart: {
    type: 'line',
    backgroundColor: 'transparent',
    spacingTop: 5,
    spacingBottom: 5,
    height: 200,
  },
  title: false,
  credits: false,
  boost: {
    enabled: true,
    useGPUTranslations: true,
  },
  xAxis: {
    type: 'datetime',
    labels: {
      format: '{value: %Y-%m-%d %H:%M}',
      style: {
        color: '$chinagray',
        fontSize: '9px',
      },
    },
    crosshair: true,
  },
  yAxis: {
    title: false,
    lineWidth: 1,
    labels: {
      formatter() {
        const series = this.chart.series;
        const unit = !isEmpty(series) ? series[0].userOptions.unit : null;
        return unit === 'sec' ? convertMillisecondsToSeconds(this.value) : convertValuByUnit(this.value, unit, true, true);
      },
      style: {
        color: '$luckygray',
        fontSize: '9px',
      },
    },
  },
  plotOptions: {
    series: {
      stickyTracking: false,
      animation: false,
      lineWidth: 1,
      marker: {
        radius: 1.5,
      },
    },
  },
  tooltip: {
    xDateFormat: '%Y-%m-%d %H:%M',
    split: false,
    shared: true,
    valueDecimals: 2,
    formatter() {
      let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm');
      this.points.forEach((point) => {
        const unit = point.series.userOptions.unit;
        const yValue = unit === 'sec' ? convertMillisecondsToSeconds(point.y) : convertValuByUnit(point.y, unit, true, true);
        tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${yValue}</b><br/>`;
      });
      return tooltip;
    },
  },
  legend: false,
  series: [],
  colors: chartColorSet,
};
export default {
  name: 'WCommonLineChart',
  props: {
    visible: Boolean,
    chartData: Object,
  },
  data() {
    return {
      options,
      legendPoints: [],
    };
  },
  watch: {
    visible(flag) {
      if (!flag) {
        this.options.series = [];
        this.legendPoints = [];
      }
    },
    chartData(data) {
      this.options.series = data.series;
      this.$nextTick(() => {
        this.setCustomTooltip('init');
      });
    },
  },
  mounted() {
    const me = this;

    Object.assign(this.options.plotOptions.series, {
      stickyTracking: false,
      point: {
        events: {
          mouseOver() {
            me.setCustomTooltip();
          },
        },
      },
      events: {
        mouseOut() {
          me.setCustomTooltip('init');
        },
      },
    });
  },
  methods: {
    /**
     * 범례에 표현할 이름을 반환한다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertLegendName(series) {
      if (isEmpty(series)) {
        return null;
      }
      return series.name;
    },
    /**
     * 차트 옆에 툴팁 겸 범례를 표현한다.
     * 1. type이 init일 경우 가장 최근의 데이터를 표현한다.
     * 2. 그 외의 데이터는 마우스 오버 했을 시 해당 포인트르르 표현한다.
     * @param  {[type]} type [description]
     * @author jda
     */
    setCustomTooltip(type) {
      if (isEmpty(this.$refs.highchart)) {
        return;
      }
      const chart = this.$refs.highchart.chart;
      const points = [];
      if (!isEmpty(type) && type === 'init') {
        chart.series.forEach((record) => {
          const point = findLast(record.points, (data) => !isEmpty(data.y));
          if (point) {
            points.push(point);
          }
        });
      } else {
        chart.series.forEach((record) => {
          const point = record.searchPoint(event, true);
          if (point) {
            points.push(point);
          } else {
            const finalPoint = findLast(record.points, (data) => !isEmpty(data.y));
            if (!isEmpty(finalPoint)) {
              points.push(finalPoint);
            }
          }
        });
      }
      this.legendPoints = points;
    },
    /**
     * 범례 보이기/숨김 처리를 한다.
     * 최대 8개의 항목만 활성화 할 수 있다.
     * @param  {[type]} series [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onClickLegend(currentSeries) {
      if (currentSeries.visible) {
        this.legendClass = 'legend-off';
        currentSeries.setVisible(false, false);
      } else {
        this.legendClass = null;
        currentSeries.setVisible(true, false);
      }
    },
    /**
     * 범례 활성화/비활성화의 className을 반환한다.
     * @param  {[type]} currentSeries [description]
     * @return {[type]}               [description]
     * @author jda
     */
    getLegendClass(currentSeries) {
      if (isEmpty(currentSeries)) {
        return null;
      }
      return currentSeries.visible ? null : 'legend-off';
    },
    /**
     * 차트의 데이터를 단위변환 한다.
     * @param  {[type]} point [description]
     * @return {[type]}       [description]
     * @author jda
     */
    convertValuByUnit(point) {
      return convertValuByUnit(point.y, null, true, false);
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/apm/components/chart/CommonLineChart.vue